import { inject, Injectable, effect } from '@angular/core';
import { Subject } from 'rxjs';
import { IdentityService } from '../identity/identity.service';
import { SettingsService } from '../settings/settings.service';
import io from 'socket.io-client';
import { Device, types as mediasoupTypes } from "mediasoup-client";
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class VoiceService {
  private authService = inject(IdentityService);
  private settingsService = inject(SettingsService);

  public eventChannel: Subject<any> = new Subject<any>();
  private token: any = null;
  private socket!: SocketIOClient.Socket;
  private device!: mediasoupTypes.Device;
  private channelId: string = "";
  private channel: any;
  private consumers: mediasoupTypes.Consumer[] = [];
  private producer!: mediasoupTypes.Producer | null;
  tracks: any[] = [];

  private recvTransport!: mediasoupTypes.Transport | null;
  private sendTransport!: mediasoupTypes.Transport | null;

  private mediaDevices: any;
  private localStream: any;

  constructor() {
    this.token = this.authService.getToken();
    console.log("Voice token is => ", this.token);
    
    //this.token = JSON.parse(sessionStorage.getItem('bearer-token') ?? '{}');

    // Reactively handle input device changes
    effect(() => {
      const inputDevice = this.settingsService.selectedInputDevice();
      this.handleInputDeviceChange(inputDevice);
    });

    // Reactively handle output device changes
    effect(() => {
      const outputDevice = this.settingsService.selectedOutputDevice();
      this.handleOutputDeviceChange(outputDevice);
    });

    // Reactively handle master volume changes for the voice audio element
    effect(() => {
      const volume = this.settingsService.masterVolume() / 100;
      const elem = document.getElementById('audio') as HTMLMediaElement;
      if (elem) {
        elem.volume = volume;
      }
    });
  }

  /*
   * Connect to voice service for given channel/room ID.
   */
  async connect(id: string) {
    try {
      if (this.socket && this.socket.connected) {
        try {
          await this.disconnect();
        } catch (e) {}
      }

      // Create the websocket.
      if (this.socket == null || this.socket.disconnected) {
        // Set channel id.
        this.channelId = id;
        console.log("Connecting to voice service..." + this.channelId);
        this.socket = io(environment.voiceUrl, { transports: ["websocket"], reconnection: true, reconnectionAttempts: 3 });
        await this.subscribeToEvents();
        this.socket.connect();
      } else {
        console.log("Voice Service Connect - Something went wrong.");
      }
    } catch (err) {
      console.log("Error in voiceService Connection");
      console.error(err);
    }
  }

  /*
   * Disconnect from voice service and clean up WebRTC transports.
   */
  public disconnect(): void {
    try {
      if (this.recvTransport) {
        try {
          this.recvTransport.close();
        } catch (e) {}
      }
      this.recvTransport = null;

      if (this.sendTransport && this.sendTransport.closed == false) {
        try {
          this.sendTransport.close();
        } catch (e) {}
      }
      this.sendTransport = null;

      if (this.producer && this.producer.closed == false) {
        try {
          this.producer.close();
        } catch (e) {}
        this.producer = null;
      }

      if (this.consumers) {
        this.consumers.forEach((consumer) => {
          try {
            consumer.close();
          } catch (e) {}
        });
      }

      this.consumers = [];
      if (this.socket) {
        try {
          this.socket.disconnect();
        } catch (e) {}
      }

      this.tracks.forEach((track) => {
        let stream;
        let elem: HTMLMediaElement = <HTMLMediaElement>document.getElementById('audio');
        if (elem && elem.srcObject != null) {
          stream = <MediaStream>elem.srcObject;
          stream.removeTrack(track);
        }
      });
      this.tracks = [];
    } catch (err) {
      // Ignore disconnect cleanup errors
    }
  }

  /**
   * Re-initialize tracks to audio element.
   */
  async reinit() {
    try {
      console.log("Re-initializing tracks.");
      this.tracks.forEach((track) => {
        let stream = new MediaStream();
        console.log("Setting stream to element.");
        let elem: HTMLMediaElement = <HTMLMediaElement>document.getElementById('audio');
        if (elem.srcObject != null)
          stream = <MediaStream>elem.srcObject;

        stream.addTrack(track);
        elem.srcObject = stream;
        
        // Apply output device sink ID
        const currentOutputDevice = this.settingsService.selectedOutputDevice();
        if (currentOutputDevice && currentOutputDevice !== 'default' && typeof (elem as any).setSinkId === 'function') {
          (elem as any).setSinkId(currentOutputDevice).catch((e: any) => {
            console.error("Failed to set sink ID during reinit", e);
          });
        }
        elem.volume = this.settingsService.masterVolume() / 100;
        //elem.play();

      });
    }
    catch (err) {
      console.error(err);
    }
  }

  startTalking() {
    this.socket.emit('resumeProducer', { id: this.channelId });
  }

  stopTalking() {
    this.socket.emit('pauseProducer', { id: this.channelId });
  }

  status() {
    this.socket.emit('getChannels', {});
  }

  /**
   * Subscribe to socket events.
   */
  private async subscribeToEvents() {
    try {
      this.socket.on('connect', () => {
        console.log("Voice service connected.");
        this.onConnect();
      });

      // Handle disconnect.
      this.socket.on('disconnect', () => {
        console.log("Voice service disconnected.");
        this.disconnect();
      });

      this.socket.on("connecting", () => {
        console.log("Socket Connecting to voice service...");
      });

      this.socket.on("connect_error", (err: any) => {
        console.log("Connection failed.");
        console.error(err);
      });

      // Client has joined a channel.
      this.socket.on('joined', (data: any) => {
        console.log("joined voice channel => ", data);
        this.channel = data;
        this.eventChannel.next(this.channel);
        this.onJoined();
      });

      // Show list of channels
      this.socket.on('getChannels', (data: any) => {
        console.log(data);
      });

      // Add peer info to channel.
      this.socket.on('addpeer', (data: any) => {
        try {
          console.log("addpeer occured.");
          let newPeer = this.channel.peers.filter((p: any) => { return p.id == data.peer.id })[0];
          if (newPeer == null) {
            console.log("Adding new peer " + data.peer.userNameSlug);
            this.channel.peers.push(data.peer);
          } else {
            console.log("Updating new peer " + data.peer.userNameSlug);
            let index = this.channel.peers.indexOf(newPeer);
            this.channel.peers[index] = data.peer;
          }

          this.eventChannel.next(this.channel);
        }
        catch (err) {
          console.error(err);
        }
      });

      // Removes peer info from channel.
      this.socket.on('removepeer', (data: any) => {
        let peer = this.channel.peers.filter((p: any) => { return p.id == data.id })[0];
        if (peer != null) {
          let index = this.channel.peers.indexOf(peer);
          this.channel.peers.splice(index, 1);
        }
        this.eventChannel.next(this.channel);
      });

      this.socket.on('gotRouterRtpCapabilities', (data: any) => {
        this.onGotRouterRtpCapabilities(data);
      });

      this.socket.on('createdConsumerTransport', (data: any) => {
        this.onCreatedConsumerTransport(data);
      });

      this.socket.on('createdProducerTransport', (data: any) => {
        this.onCreatedProducerTransport(data);
      });

      this.socket.on('newProducer', (data: any) => {
        // will need to send new peer's id to get their producer
        console.log("producer peer id " + data.id);
        console.log(data.userNameSlug + " wants you to hear them.");
        if (this.recvTransport && data.userNameSlug != this.token.userNameSlug.toLowerCase().replace(' ', '')) {
          console.log(data.userNameSlug + " is available to hear.");
          const { rtpCapabilities } = this.device;
          this.socket.emit('consume', { id: this.channelId, transportId: this.recvTransport.id, rtpCapabilities, producerPeerId: data.id });
        }
      });

      this.socket.on('consuming', (data: any) => {
        this.onConsuming(data);
      });

      this.socket.on('producing', (data: any) => {

      });

      this.socket.on('istalking', (data: any) => {
        console.log("isTalking ", data);
        let peer = this.channel.peers.find((p: any) => p.id === data.id);
        if (peer) {
          peer.isTalking = true;
        } else {
          console.log("isTalking peer not found.");
        }
        this.eventChannel.next(this.channel);
      });

      this.socket.on('notalking', (data: any) => {
        let peer = this.channel.peers.find((p: any) => p.id === data.id);
        if (peer) {
          peer.isTalking = false;
        }
        this.eventChannel.next(this.channel);
      });
    }
    catch (err) {
      console.log("Error in subscribeToEvents");
      console.error(err);
    }
  }

  private async loadDevice(routerRtpCapabilities: any) {
    try {
      if (this.device == null || this.device.loaded == false) {
        this.device = new Device();
        await this.device.load({ routerRtpCapabilities });
      }
    }
    catch (err: any) {
      if (err.name === 'UnsupportedError') {
        console.error('Browser is not supported.');
      } else {
        console.error(err);
      }
    }
  }

  private async loadUserMedia() {
    try {
      const selectedDeviceId = this.settingsService.selectedInputDevice();
      const constraints: MediaStreamConstraints = {
        video: false,
        audio: selectedDeviceId && selectedDeviceId !== 'default' 
          ? { deviceId: { exact: selectedDeviceId } } 
          : true
      };

      this.mediaDevices = await navigator.mediaDevices.getUserMedia(constraints);
      console.log("Acquired media devices with constraints => ", constraints, this.mediaDevices);
    }
    catch (err) {
      console.error("Error loading user media with selected device:", err);
      
      // Fallback
      try {
        this.mediaDevices = await navigator.mediaDevices.getUserMedia({
          video: false,
          audio: true
        });
      } catch (fallbackErr) {
        console.error("Fallback getUserMedia failed => ", fallbackErr);
      }
    }
  }

  /**
   * OnConnect event that triggers join of channel.
   */
  private async onConnect() {
    try {
      console.log("Connected to voice server, joining channel...");
      console.log("The token => ", this.token);
      this.socket.emit('join', { id: this.channelId, userNameSlug: this.token.userNameSlug.toLowerCase() });
    }
    catch (err) {
      console.error(err);
    }
  }

  private async onJoined() {
    try {
      console.log("Joined channel, requesting router rtp capabilities");
      this.socket.emit('getRouterRtpCapabilities', { id: this.channelId });
    }
    catch (err) {
      console.error(err);
    }
  }

  /**
   * OnGotRouterRtpCapabilities event that triggers createProducerTransport request.
   * @param routerRtpCapabilities 
   */
  private async onGotRouterRtpCapabilities(routerRtpCapabilities: any) {
    try {
      console.log("Router rtp capabilities recv => ", routerRtpCapabilities);
      await this.loadDevice(routerRtpCapabilities);
      await this.loadUserMedia();

      // Request Create Consumer Transport
      console.log("Send createConsumerTransport Request");
      this.socket.emit('createConsumerTransport', { id: this.channelId });

      // Request Create Producer Transport
      this.socket.emit('createProducerTransport', { id: this.channelId });
    }
    catch (err) {
      console.error(err);
    }
  }

  private async onCreatedProducerTransport(transportOptions: any) {
    try {
      console.log("Creating producer transport with options:", transportOptions);
      this.sendTransport = this.device.createSendTransport(transportOptions);

      this.sendTransport.on('connect', async ({ dtlsParameters }, callback, errback) => {
        try {
          console.log("Producer transport connected with dtlsParameters:", dtlsParameters);
          await this.socket.emit('connectProducerTransport', { id: this.channelId, transportId: this.sendTransport!.id, dtlsParameters: dtlsParameters });
          callback();
        }
        catch (err) {
          console.error(err);
        }
      });

      this.sendTransport.on('produce', ({ kind, rtpParameters }) => {
        console.log("produce kind => ", kind);
        console.log("produce params => ", rtpParameters);
        this.socket.emit('produce', {
          id: this.channelId,
          transportId: this.sendTransport!.id,
          kind: kind,
          rtpParameters: rtpParameters
        });

        // Request Create Consumer Transport
        //console.log("Send createConsumerTransport Request");
        //this.socket.emit('createConsumerTransport', { id: this.channelId });
      });

      this.sendTransport.on('connectionstatechange', (state) => {
        switch (state) {
          case 'connecting':
            console.log("Producer Transport connecting...");
            break;

          case 'connected':
            console.log("Producer Transport connected");
            //let elem: HTMLMediaElement = <HTMLMediaElement>document.getElementById('local');
            //elem.srcObject = this.mediaDevices;
            //elem.play();
            //console.log("Element should be playing locally at this time.");
            break;

          case 'failed':
            console.log("Transport connection failed");
            this.sendTransport!.close();
            break;

          default: break;
        }
      });

      // Creates our producer. Will need to modify for video.
      if (this.mediaDevices) {

        this.localStream = await this.mediaDevices.getAudioTracks();

        console.log("producer transport calling produce");
        this.producer = await this.sendTransport.produce({
          track: this.localStream[0]
        });

      }

    }
    catch (err: any) {
      if (err?.name === 'AwaitQueueStoppedError' || err?.message?.includes('queue stopped')) {
        return;
      }
      console.error(err);
    }
  }

  /**
   * Consumer Transport created and ready to consume producers.
   * @param transportOptions 
   */
  private async onCreatedConsumerTransport(transportOptions: any) {
    try {
      console.log("onCreatedConsumerTransport called.");

      this.recvTransport = this.device.createRecvTransport(transportOptions);
      this.recvTransport.on('connect', async ({ dtlsParameters }, callback, errback) => {
        console.log("Consumer transport is connected to server.");
        this.socket.emit('connectConsumerTransport', { id: this.channelId, transportId: this.recvTransport!.id, dtlsParameters: dtlsParameters });
        callback();
      });

      /*
      // Not a real setup.
      this.recvTransport.on('consume', ({ kind, rtpParameters }) => {
        console.log("Consumer tranport received consume.");
      });*/

      this.recvTransport.on('connectionstatechange', (state) => {
        switch (state) {
          case 'connecting':
            console.log("Consumer Transport connecting...");
            break;

          case 'connected':
            console.log("Consumer Transport connected and informing server to resume.");
            this.socket.emit('resume', { id: this.channelId });
            break;

          case 'failed':
            console.log("Consumer Transport connection failed");
            this.recvTransport!.close();
            break;

          default: break;
        }
      });

      const { rtpCapabilities } = this.device;
      this.socket.emit('consumeAll', { id: this.channelId, transportId: this.recvTransport.id, rtpCapabilities });
    }
    catch (err: any) {
      if (err?.name === 'AwaitQueueStoppedError' || err?.message?.includes('queue stopped')) {
        return;
      }
      console.error(err);
    }
  }

  /**
   * Incoming data to be consumed.
   * @param data 
   */
  private async onConsuming(data: any) {
    try {

      console.log("Server consumer created");
      //let codecOptions = {};
      const consumer = await this.recvTransport!.consume({
        id: data.id,
        producerId: data.producerId,
        kind: data.kind,
        rtpParameters: data.rtpParameters
      });

      consumer.on('trackended', () => {
        console.log("Consumer track ended, removing track.");
        let index = this.tracks.indexOf(consumer.track);
        this.tracks.slice(index, 1);
        let elem: HTMLMediaElement = <HTMLMediaElement>document.getElementById('audio');
        if (elem.srcObject != null)
          stream = <MediaStream>elem.srcObject;

        stream.removeTrack(consumer.track);
      });

      this.consumers.push(consumer);

      let stream = new MediaStream();
      console.log("Setting stream to element.");
      let elem: HTMLMediaElement = <HTMLMediaElement>document.getElementById('audio');
      if (elem.srcObject != null)
        stream = <MediaStream>elem.srcObject;

      this.tracks.push(consumer.track);
      stream.addTrack(consumer.track);
      elem.srcObject = stream;
      
      // Apply output device sink ID
      const currentOutputDevice = this.settingsService.selectedOutputDevice();
      if (currentOutputDevice && currentOutputDevice !== 'default' && typeof (elem as any).setSinkId === 'function') {
        (elem as any).setSinkId(currentOutputDevice).catch((e: any) => {
          console.error("Failed to set sink ID during consuming", e);
        });
      }

      elem.volume = this.settingsService.masterVolume() / 100;
      elem.play();

    }
    catch (err: any) {
      if (err?.name === 'AwaitQueueStoppedError' || err?.message?.includes('queue stopped')) {
        return;
      }
      console.error(err);
    }
  }

  private async handleInputDeviceChange(deviceId: string) {
    if (!this.producer || this.producer.closed) {
      return;
    }

    try {
      console.log(`Input device changed to: ${deviceId}. Replacing active audio track...`);
      
      // Stop old tracks first
      if (this.localStream) {
        this.localStream.forEach((track: any) => {
          try { track.stop(); } catch (e) {}
        });
      }

      // Get new media stream
      const constraints: MediaStreamConstraints = {
        video: false,
        audio: deviceId !== 'default' ? { deviceId: { exact: deviceId } } : true
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      const newTrack = stream.getAudioTracks()[0];
      
      // Replace the track on the active producer
      await this.producer.replaceTrack({ track: newTrack });
      
      // Update our references
      this.localStream = [newTrack];
      this.mediaDevices = stream;
      console.log("Active audio track replaced successfully.");
    } catch (err) {
      console.error('Failed to replace active audio track on device change:', err);
    }
  }

  private async handleOutputDeviceChange(deviceId: string) {
    try {
      const elem = document.getElementById('audio') as HTMLMediaElement;
      if (elem && typeof (elem as any).setSinkId === 'function') {
        const sinkId = deviceId === 'default' ? '' : deviceId;
        console.log(`Setting audio output device sink ID to: ${sinkId}`);
        await (elem as any).setSinkId(sinkId);
      }
    } catch (err) {
      console.error('Failed to set audio output device sink ID:', err);
    }
  }
}
